import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export enum CallStatus {
    IDLE = 'IDLE',
    REQUESTING = 'REQUESTING',
    CONNECTING = 'CONNECTING',
    CONNECTED = 'CONNECTED',
    DISCONNECTING = 'DISCONNECTING',
    ERROR = 'ERROR',
}

export interface CallParticipant {
    id: string
    name: string
    stream?: MediaStream
    audioEnabled: boolean
    videoEnabled: boolean
}

export interface CallState {
    status: CallStatus
    callId: string | null
    participants: CallParticipant[]
    isCaller: boolean
    localStream: MediaStream | null
    error: string | null
    isMinimized: boolean
    showCallControls: boolean
}

const initialState: CallState = {
    status: CallStatus.IDLE,
    callId: null,
    participants: [],
    isCaller: false,
    localStream: null,
    error: null,
    isMinimized: false,
    showCallControls: false,
}

export const callSlice = createSlice({
    name: 'call',
    initialState,
    reducers: {
        setCallStatus: (state, action: PayloadAction<CallStatus>) => {
            state.status = action.payload
        },
        setCallId: (state, action: PayloadAction<string | null>) => {
            state.callId = action.payload
        },
        addParticipant: (state, action: PayloadAction<CallParticipant>) => {
            // Don't add if participant already exists
            if (!state.participants.find(p => p.id === action.payload.id)) {
                state.participants.push(action.payload)
            }
        },
        updateParticipant: (state, action: PayloadAction<{ id: string, updates: Partial<CallParticipant> }>) => {
            const index = state.participants.findIndex(p => p.id === action.payload.id)
            if (index !== -1) {
                state.participants[index] = {
                    ...state.participants[index],
                    ...action.payload.updates
                }
            }
        },
        removeParticipant: (state, action: PayloadAction<string>) => {
            state.participants = state.participants.filter(p => p.id !== action.payload)
        },
        setLocalStream: (state, action: PayloadAction<MediaStream | null>) => {
            state.localStream = action.payload
        },
        setIsCaller: (state, action: PayloadAction<boolean>) => {
            state.isCaller = action.payload
        },
        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload
            if (action.payload) {
                state.status = CallStatus.ERROR
            }
        },
        setIsMinimized: (state, action: PayloadAction<boolean>) => {
            state.isMinimized = action.payload
        },
        setShowCallControls: (state, action: PayloadAction<boolean>) => {
            state.showCallControls = action.payload
        },
        resetCall: (state) => {
            // Clean up all state except local stream, which needs special handling
            const localStream = state.localStream
            Object.assign(state, { ...initialState, localStream })
        },
        resetCallFull: (state) => {
            // Full reset including local stream
            Object.assign(state, initialState)
        }
    },
})

export const {
    setCallStatus,
    setCallId,
    addParticipant,
    updateParticipant,
    removeParticipant,
    setLocalStream,
    setIsCaller,
    setError,
    setIsMinimized,
    setShowCallControls,
    resetCall,
    resetCallFull
} = callSlice.actions

export default callSlice.reducer 